// Helpers for one-tap customer communication (call / WhatsApp / email / copy).
// Keeps link building and clipboard behaviour consistent across the Customer
// list and detail pages.

const DEFAULT_COUNTRY_CODE = "91"; // India — matches backend phone normalization.

export function normalizePhoneForTel(phone) {
  if (!phone) return "";
  // Keep a single leading + (if present) and digits only.
  const raw = String(phone).trim();
  const hasPlus = raw.startsWith("+");
  const digits = raw.replace(/\D/g, "");
  return digits ? `${hasPlus ? "+" : ""}${digits}` : "";
}

export function toTelHref(phone) {
  const n = normalizePhoneForTel(phone);
  return n ? `tel:${n}` : "";
}

/** Digits-only, country-code prefixed number suitable for wa.me links. */
export function toWhatsAppDigits(phone, defaultCountryCode = DEFAULT_COUNTRY_CODE) {
  if (!phone) return "";
  let n = String(phone).replace(/\D/g, "");
  n = n.replace(/^0+/, "");
  if (!n) return "";
  // A bare local 10-digit number gets the default country code prefixed.
  if (n.length === 10) n = `${defaultCountryCode}${n}`;
  return n;
}

export function toWhatsAppHref(phone, text) {
  const digits = toWhatsAppDigits(phone);
  if (!digits) return "";
  const query = text ? `?text=${encodeURIComponent(text)}` : "";
  return `https://wa.me/${digits}${query}`;
}

export function toMailtoHref(email, { subject, body } = {}) {
  const e = String(email || "").trim();
  if (!e) return "";
  const params = [];
  if (subject) params.push(`subject=${encodeURIComponent(subject)}`);
  if (body) params.push(`body=${encodeURIComponent(body)}`);
  return `mailto:${e}${params.length ? `?${params.join("&")}` : ""}`;
}

export function buildWhatsAppGreeting(name) {
  const first = String(name || "").trim().split(/\s+/)[0];
  return first ? `Hi ${first}, ` : "Hi, ";
}

/** Single-line, paste-friendly summary of a customer's contact details. */
export function buildContactCard(customer = {}) {
  const lines = [
    customer.customerName,
    customer.companyName && customer.companyName !== customer.customerName
      ? customer.companyName
      : null,
    customer.cust_ID ? `ID: ${customer.cust_ID}` : null,
    customer.phone1 ? `Phone: ${customer.phone1}` : null,
    customer.phone2 ? `Alt: ${customer.phone2}` : null,
    customer.email ? `Email: ${customer.email}` : null,
    customer.gstNumber ? `GST: ${customer.gstNumber}` : null,
  ].filter(Boolean);
  return lines.join("\n");
}

export async function copyText(text) {
  const value = String(text ?? "").trim();
  if (!value) return false;

  try {
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch (_) {
    // fall through to legacy path below
  }

  try {
    const ta = document.createElement("textarea");
    ta.value = value;
    ta.style.position = "fixed";
    ta.style.left = "-9999px";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch (_) {
    return false;
  }
}
